import { useRef, useEffect, useCallback } from "react";
import { cities } from "../../../config/appData";
import { getCityImageUrl } from "../CitiesSection";
import "./ExploreMoreCities.css";

const SCROLL_SPEED = 1;
const RESUME_AUTO_SCROLL_AFTER_MS = 4000;

function ExploreMoreCities({ currentCity = "", onCitySelect, autoScroll = true }) {
  const scrollRef = useRef(null);
  const autoScrollPausedRef = useRef(false);
  const resumeTimeoutRef = useRef(null);

  const otherCities = (cities || []).filter(
    (c) => String(c).toLowerCase() !== String(currentCity || "").toLowerCase(),
  );

  useEffect(() => {
    if (!autoScroll || otherCities.length === 0) return;
    const el = scrollRef.current;
    if (!el) return;

    let rafId = null;
    const tick = () => {
      if (autoScrollPausedRef.current) {
        rafId = requestAnimationFrame(tick);
        return;
      }
      el.scrollLeft += SCROLL_SPEED;
      const maxScroll = el.scrollWidth - el.clientWidth;
      if (maxScroll > 0 && el.scrollLeft >= maxScroll - 2) {
        el.scrollLeft = 0;
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => {
      if (rafId != null) cancelAnimationFrame(rafId);
      if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
    };
  }, [autoScroll, otherCities.length]);

  const pauseAutoScroll = useCallback(() => {
    autoScrollPausedRef.current = true;
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
    resumeTimeoutRef.current = setTimeout(() => {
      autoScrollPausedRef.current = false;
      resumeTimeoutRef.current = null;
    }, RESUME_AUTO_SCROLL_AFTER_MS);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = () => pauseAutoScroll();
    const onTouchStart = () => pauseAutoScroll();
    el.addEventListener("wheel", onWheel, { passive: true });
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("touchstart", onTouchStart);
    };
  }, [pauseAutoScroll]);

  if (otherCities.length === 0) return null;

  return (
    <section id="explore-more-cities" className="explore-more-cities-section">
      <div className="explore-more-cities-container">
        <span className="explore-more-label">Cities</span>
        <h2 className="explore-more-cities-title">Explore more cities</h2>
        <div
          ref={scrollRef}
          className="explore-more-cities-scroll"
          role="region"
          aria-label="Explore more cities carousel"
        >
          <div className="explore-more-cities-track">
            {otherCities.map((city) => (
              <button
                key={city}
                type="button"
                className="explore-more-city-card"
                onClick={() => onCitySelect?.(city)}
              >
                <div className="explore-more-city-image-wrapper">
                  <img
                    src={getCityImageUrl(city)}
                    alt={`${city} wedding venues`}
                    className="explore-more-city-image"
                    loading="lazy"
                  />
                  <div className="explore-more-city-overlay" />
                  <span className="explore-more-city-name">{city}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default ExploreMoreCities;
